import { ColorX, LocaleX, ZMath } from "IZOX/STATIC";
import { HStack, XButton, XDiv, XText } from "IZOX/_X";
import _ from "lodash";
import { useEffect, useRef, useState } from "react";
import { FaArrowDown19, FaArrowDownAZ, FaArrowDownShortWide, FaArrowUp19, FaArrowUpAZ, FaArrowUpShortWide } from "react-icons/fa6";

/**
 * @typedef {import("../Second/QTHeader").qtableschema} qtableschema
 * @typedef {import("../../QTable").stylesparams}  stylesparams
 * 
 * @typedef {Object} qthcellparam
 * @property {qtableschema} schema
 * @property {Function} _onRectSet
 * @property {*} addOns 
 * @property {Number} idx
 * @property {Object.<string, stylesparams>} Styles
 * @property {Function} onSortChange
 * @property {any} sorter
 * 
 * @param {qthcellparam} param0 
 * @returns 
 */
function QTHCell({
  schema = {},
  addOns = {},
  _onRectSet = null,
  idx = null,
  Styles = {},
  onSortChange = undefined,
  sorter = undefined,
  showCol = undefined,
  colDirty = undefined,
  darkMode = false,
  _passedWidth,
  canResize = true,
  maxColWidth,
  ...others
}){

  const [tabWidth, setTabWidth] = useState(_passedWidth);
  const [hover, setHover] = useState(false);
  const resizableRef = useRef(null);
  const isResizing = useRef(false);

  useEffect(() => {
    setTabWidth(_passedWidth);
  }, [_passedWidth]);

  let {Header, width} = schema;

  function _renderHeaderCell(_schema, _addOns){
    let {label} = _schema;
    return (
      <XText {...Styles.headerCellText} ellipsisMode="ellipsis">
        {LocaleX.Parse(label)}
      </XText>
    );
  };

  const handleMouseDown = (e) => {
    isResizing.current = true;
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleMouseMove = (e) => {
    if (!isResizing.current) return;

    const newWidth = e.clientX - resizableRef.current.getBoundingClientRect().left;
    let _maxWidth = Math.min(maxColWidth, maxWidth);
    let _width = ZMath.Cap(newWidth, minWidth, _maxWidth);
    setTabWidth(_width);
    _onRectSet?.(idx, _width);
  };

  const handleMouseUp = () => {
    setHover(false);
    isResizing.current = false;
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  };

  const onMouseEnter = () => {
    setHover(true);
  };

  const onMouseLeave = () => {
    if(!isResizing.current){
      setHover(false);
    }
  };

  let {name, sortable = true, 
    resizable = _.isUndefined(schema.width), 
    minWidth = 50, maxWidth = Number.MAX_SAFE_INTEGER,
    sortComparer} = schema;

  resizable = canResize && resizable;

  let sort = sorter?.[name];
  let iconStyle = {color: ColorX.CSS("greyOut")};
  let icon = sortable ? 
    (sort === "asc" ? 
      (sortComparer ? <FaArrowDownShortWide style={iconStyle}/> : 
        (schema.format === "number" ? <FaArrowDown19 style={iconStyle}/> : <FaArrowDownAZ style={iconStyle}/>)) :
    (sort === "desc" ? 
      (sortComparer ? <FaArrowUpShortWide style={iconStyle}/> : 
        (schema.format === "number" ? <FaArrowUp19 style={iconStyle}/> : <FaArrowUpAZ style={iconStyle}/>))
      : null)) 
    : null;

  if(!showCol) width = 0;

  const _tabWidth = tabWidth ?? 0;

  return (
     <HStack fullHeight forwardRef={resizableRef} lockWidth={_tabWidth}
        position="relative" overflow="visible">
        <XButton tooltip={LocaleX.Parse(schema.label)}
          lockWidth={_passedWidth} delayAfterPress={10} showLoading={false}
          onPress={sortable ? () => onSortChange(name) : undefined} notInjectColor
          padding={0}
          flexGrow={!_.isUndefined(width) ? undefined : 1} flexBasis={!_.isUndefined(width) ? undefined : 1} 
          {...Styles.headerCell}>
          <HStack spacing={5}>
            {Header?.(schema, addOns) 
            ?? _renderHeaderCell?.(schema, addOns)}
              {icon}
          </HStack>
        </XButton>
        {resizable && _passedWidth > 0 &&
        <XDiv position="absolute" style={{right: 0}} fullHeight width={0} 
          overflow="visible" zIndex={1}>
          <XDiv 
            colorTransition
            onMouseEnter={onMouseEnter} 
            onMouseLeave={onMouseLeave}
            onMouseDown={handleMouseDown}
            cursor="ew-resize"
            width={hover ? 4 : 2}
            mL={hover ? -2 : -1}
            fullHeight
            bgColor={
              hover ? ColorX.DayNight({day: "cmBlue", night: "dmWhite"}) : ColorX.CSS("greyOut", 0.05)}
          />
        </XDiv>}
      </HStack>
  );
}

export default QTHCell;